import React from "react";
import { Card, CardMedia, Chip, Box, Typography, Tooltip } from "@mui/material";
import { EventNote, Female, Male, Scale } from "@mui/icons-material";
import { IPetPopulated, IPets } from "../types";
import { getDefaultPetImage, formatAge } from "../utils";

interface MiniPetCardProps {
  data: IPetPopulated;
  action?: () => void;
  setData: (data: IPets) => void;
  width?: number | string;
  height?: number | string;
  imageSize?: number; // lado da imagem
  elevation?: number;
  showSpecies?: boolean;
  showTutor?: boolean;
  showAge?: boolean;
  showWeight?: boolean;
  dense?: boolean;
  sx?: any; // permitir overrides rápidos
  className?: string;
}

const MiniPetCard: React.FC<MiniPetCardProps> = ({
  data,
  action,
  setData,
  width = 340,
  height = 180,
  imageSize = 96,
  elevation = 3,
  showSpecies = true,
  showTutor = true,
  showAge = true,
  showWeight = true,
  dense = false,
  sx,
  className,
}) => {
  const speciesLabel = data.species || "Pet";
  // backend envia objeto {years, months}; se vier outro formato mostramos fallback
  const ageLabel = formatAge((data as any).age);
  const genderIsFemale = data.gender === "Fêmea" || data.gender === "Femea";

  const handleActivate = () => {
    setData(data as unknown as IPets);
    action?.();
  };

  return (
    <Card
      role="button"
      aria-label={`Pet ${data.name}`}
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivate();
        }
      }}
      sx={{
        width,
        height,
        borderRadius: 2,
        p: dense ? 1 : 1.5,
        boxShadow: elevation,
        display: "flex",
        cursor: "pointer",
        outline: "none",
        transition: "all .18s ease",
        position: "relative",
        background: (theme) =>
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#fff",
        "&:hover": {
          boxShadow: 8,
          transform: "translateY(-2px)",
        },
        "&:focus-visible": {
          boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}55`,
        },
        ...sx,
      }}
      className={className}
    >
      {/* Imagem */}
      <Box
        sx={{
          width: imageSize,
          minWidth: imageSize,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mr: dense ? 1 : 2,
        }}
      >
        <CardMedia
          component="img"
          src={
            (data as any).photo ||
            (data as any).foto ||
            getDefaultPetImage(Math.floor(Math.random() * 100))
          }
          alt={data.name}
          loading="lazy"
          style={{
            width: imageSize,
            height: "100%",
            objectFit: "cover",
            borderRadius: 12,
            backgroundColor: "rgba(0,0,0,0.06)",
          }}
        />
      </Box>

      {/* Conteúdo */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: dense ? 0.5 : 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              noWrap
              title={data.name}
              sx={{ lineHeight: 1.15 }}
            >
              {data.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={data.breed}
              sx={{ display: "block" }}
            >
              {data.breed || "Raça não informada"}
            </Typography>
            {showTutor && (
              <Typography
                variant="caption"
                color="text.disabled"
                noWrap
                title={(data as any)?.tutor?.name}
              >
                Tutor: {(data as any)?.tutor?.name || "Não informado"}
              </Typography>
            )}
          </Box>
          {showSpecies && (
            <Chip
              label={speciesLabel}
              size="small"
              color="primary"
              sx={{ ml: 1, fontSize: 10, fontWeight: 600 }}
            />
          )}
        </Box>

        {/* Linha de informações */}
        <Box
          sx={{
            display: "flex",
            gap: dense ? 0.5 : 1,
            mt: "auto",
            flexWrap: "wrap",
          }}
        >
          {showAge && (
            <Tooltip title={`Idade: ${ageLabel}`} arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid",
                  borderColor: "divider",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1.5,
                  minWidth: 70,
                  justifyContent: "center",
                }}
              >
                <EventNote fontSize="inherit" style={{ marginRight: 4 }} />
                <Typography variant="caption" fontWeight={500} noWrap>
                  {ageLabel}
                </Typography>
              </Box>
            </Tooltip>
          )}

          <Tooltip title={`Sexo: ${data.gender || "Indeterminado"}`} arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid",
                borderColor: "divider",
                px: 1,
                py: 0.5,
                borderRadius: 1.5,
                minWidth: 64,
                justifyContent: "center",
              }}
            >
              {genderIsFemale ? (
                <Female fontSize="small" />
              ) : data.gender === "Macho" ? (
                <Male fontSize="small" />
              ) : (
                <Male fontSize="small" />
              )}
              <Typography variant="caption" fontWeight={500} ml={0.5} noWrap>
                {genderIsFemale
                  ? "Fêmea"
                  : data.gender === "Macho"
                    ? "Macho"
                    : "Indet."}
              </Typography>
            </Box>
          </Tooltip>
          {showWeight && (
            <Tooltip
              title={data.weight ? `${data.weight} kg` : "Peso não informado"}
              arrow
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid",
                  borderColor: "divider",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1.5,
                  minWidth: 56,
                  justifyContent: "center",
                }}
              >
                <Scale fontSize="inherit" style={{ marginRight: 4 }} />
                <Typography variant="caption" fontWeight={500} noWrap>
                  {data.weight ? `${data.weight}kg` : "--"}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default MiniPetCard;
